import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/database';
import type { Session } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Debug configuration
const DEBUG = true;
const DEBUG_VERBOSE = true;

// Enhanced logging utility
const debugLog = (area: string, message: string, data?: any) => {
  if (DEBUG) {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] [Supabase:${area}] ${message}`;
    
    if (DEBUG_VERBOSE) {
      console.group(logMessage);
      if (data) {
        console.dir(data, { depth: null });
      }
      console.groupEnd();
    } else {
      console.log(logMessage, data ? data : '');
    }
  }
};

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

debugLog('init', 'Initializing client', {
  url: supabaseUrl,
  hasAnonKey: !!supabaseAnonKey,
  env: import.meta.env.MODE
});

// Debug environment variables explicitly
console.log('🔍 Environment Debug:', {
  VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
  VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY ? 'present' : 'missing',
  MODE: import.meta.env.MODE,
  ALL_ENV: import.meta.env
});

// Configure Supabase client with enhanced session handling
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false, // Disable automatic URL detection to prevent PKCE errors
    storageKey: 'cupshup.auth.token',
    storage: localStorage,
    // Remove flowType to let Supabase auto-detect the flow
    debug: true // Enable debug logs
  },
  global: {
    headers: { 'x-client-info': 'cupshup-ai-app' }
  }
});

let lastRefreshAttempt = 0;
let isRefreshing = false;
const REFRESH_COOLDOWN = 30000; // 30 seconds between refresh attempts
const LOCK_TIMEOUT = 5000; // 5 seconds timeout for lock acquisition

// Export a function to check and refresh session if needed
export const checkAndRefreshSession = async () => {
  const now = Date.now();
  
  // Prevent concurrent refresh attempts
  if (isRefreshing) {
    debugLog('sessionCheck', 'Refresh already in progress, skipping');
    return null;
  }

  debugLog('sessionCheck', 'Starting session check', {
    timeSinceLastRefresh: now - lastRefreshAttempt,
    lastRefreshAttempt: new Date(lastRefreshAttempt).toISOString()
  });

  try {
    isRefreshing = true;
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      debugLog('sessionCheck', 'Error getting session', { error });
      throw error;
    }
    
    if (session) {
      debugLog('sessionCheck', 'Session found', {
        userId: session.user?.id,
        expiresAt: new Date(session.expires_at! * 1000).toISOString(),
        accessToken: session.access_token ? 'present' : 'missing',
        refreshToken: session.refresh_token ? 'present' : 'missing'
      });

      // If session exists but is close to expiring, refresh it
      const expiresAt = session.expires_at;
      if (expiresAt) {
        const nowInSeconds = Math.floor(now / 1000);
        const timeUntilExpiry = expiresAt - nowInSeconds;
        
        debugLog('sessionCheck', 'Expiry status', {
          timeUntilExpiry: `${timeUntilExpiry} seconds`,
          expiresAt: new Date(expiresAt * 1000).toISOString(),
          currentTime: new Date(nowInSeconds * 1000).toISOString()
        });

        // If less than 5 minutes until expiry and not in cooldown, refresh the session
        if (timeUntilExpiry < 300 && (now - lastRefreshAttempt) > REFRESH_COOLDOWN) {
          debugLog('sessionCheck', 'Attempting session refresh', {
            reason: 'Near expiry',
            timeUntilExpiry: `${timeUntilExpiry} seconds`
          });

          const refreshPromise = new Promise<Session>((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error('Session refresh timeout'));
            }, LOCK_TIMEOUT);

            supabase.auth.refreshSession()
              .then(({ data: { session: newSession }, error: refreshError }) => {
                clearTimeout(timeout);
                if (refreshError) throw refreshError;
                if (!newSession) throw new Error('No session returned after refresh');
                return resolve(newSession);
              })
              .catch(reject);
          });

          try {
            lastRefreshAttempt = now;
            const newSession = await refreshPromise;
            
            debugLog('sessionCheck', 'Session refreshed successfully', {
              newExpiresAt: new Date(newSession.expires_at! * 1000).toISOString()
            });
            
            return newSession;
          } catch (refreshError) {
            debugLog('sessionCheck', 'Refresh error', { error: refreshError });
            throw refreshError;
          }
        }
      }
      
      return session;
    }
    
    debugLog('sessionCheck', 'No session found');
    return null;
  } catch (error) {
    debugLog('sessionCheck', 'Error during session check', { error });
    return null;
  } finally {
    isRefreshing = false;
  }
};

// Add version tracking for debugging
debugLog('init', 'Setup complete', {
  clientVersion: '1.0.0',
  exports: {
    hasSupabase: !!supabase,
    hasCheckAndRefresh: !!checkAndRefreshSession
  }
});

export async function checkDuplicatePhoneNumber(
  phoneNumber: string, 
  brand?: string
): Promise<boolean> {
  try {
    console.log(`[DEBUG] checkDuplicatePhoneNumber called with phoneNumber: ${phoneNumber}, brand: ${brand}`);
    
    let query = supabase
      .from('tasks')
      .select('id, phone_number, activity_id!inner(brand)')
      .eq('phone_number', phoneNumber);

    // If brand is specified (Spencers, Snabbit, Handpickd, or Pronto), filter by brand
    if (brand && (brand === 'Spencers' || brand === 'Snabbit' || brand === 'Handpickd' || brand === 'Pronto')) {
      query = query.eq('activity_id.brand', brand);
      console.log(`[DEBUG] Filtering by brand: ${brand}`);
    }

    const { data, error } = await query.limit(1);

    if (error) {
      console.error('[ERROR] Database error in checkDuplicatePhoneNumber:', error);
      throw error;
    }
    
    console.log(`[DEBUG] Query returned ${data?.length || 0} results:`, data);
    const isDuplicate = data && data.length > 0;
    console.log(`[DEBUG] isDuplicate result: ${isDuplicate}`);
    
    return isDuplicate;
  } catch (error) {
    console.error('[ERROR] Exception in checkDuplicatePhoneNumber:', error);
    // Re-throw the error so the calling code can handle it properly
    throw error;
  }
}

export default supabase;